import flatten from "flat";
import fp from "lodash/fp";
import { useDispatch, useSelector } from "react-redux";
import { getFormSyncErrors, getFormValues, touch } from "redux-form";

import { useCollectAllTabErrors } from "../containers/application/applicationEdit/useSubmitApp/useCollectTabError";

const BUSINESS_INFO_FIELDS = [
	"keyed",
	"swiped",
	"eCommerce",
	"minDaysToDelivery",
	"avgDaysToDelivery",
	"maxDaysToDelivery",
	"whoWasPreviousProcessor",
	"whoDoYouUseAThirdPartyForFulfillment",
	"creditDebitCardTransactionsAreSettledTypeIfOther",
	"refundPolicyIfOther",
	"monthlyAMEX",
	"monthlyVMCD",
	"avgTicketAMEX",
	"avgTicketVMCD",
	"highTicketAMEX",
	"highTicketVMCD",
];

const PRIMARY_CONTACTS = ["phone", "email"];
const OWNER_CONTACTS = [
	"ownerPrincipalSection.owners.0.emailAddress",
	"ownerPrincipalSection.owners.0.homeMobilePhone",
];

type UseRequestBusinessAndOwnerInfoValidation = {
	formNames: string[];
	principalForm: string;
	businessForm: string;
};

export const useRequestBusinessAndOwnerInfoValidation = ({
	formNames,
	principalForm,
	businessForm,
}: UseRequestBusinessAndOwnerInfoValidation) => {
	const dispatch = useDispatch();
	const collectAllTabErrors = useCollectAllTabErrors();

	const principalFormValuesSelector = getFormValues(principalForm);
	const formValues = useSelector<any, any>(principalFormValuesSelector);

	const principalFormErrorsSelector = getFormSyncErrors(principalForm);
	const businessFormErrorsSelector = getFormSyncErrors(businessForm);
	const principalFormErrors = useSelector<any, any>(
		principalFormErrorsSelector,
	);
	const businessFormErrors = useSelector<any, any>(
		businessFormErrorsSelector,
	);

	return async () => {
		const isPrimaryContactTheSame =
			formValues?.isPrimaryContactTheSame === "true";

		const principalFieldsToValidate = isPrimaryContactTheSame
			? OWNER_CONTACTS
			: PRIMARY_CONTACTS;

		const principalErrors = fp.flow(
			flatten,
			fp.pick(principalFieldsToValidate),
		)(principalFormErrors);

		const businessInfoErrors = fp.flow(
			flatten,
			fp.pick(BUSINESS_INFO_FIELDS),
		)(businessFormErrors);

		const fieldsWithErrorPrincipal = Object.keys(principalErrors);
		dispatch(touch(principalForm, ...fieldsWithErrorPrincipal));

		const fieldsWithErrorBusiness = Object.keys(businessInfoErrors);
		dispatch(touch(businessForm, ...fieldsWithErrorBusiness));

		const principalError = Object.values(fieldsWithErrorPrincipal).some(
			error => error,
		)
			? "Validation error. Tab: Owner / Principal"
			: undefined;

		const businessInfoError = Object.values(businessInfoErrors).some(
			error => error,
		)
			? "Validation error. Tab: Business Info"
			: undefined;

		const errors = await collectAllTabErrors(formNames);

		const error =
			errors.find(er => er) || businessInfoError || principalError;

		return error ? Promise.reject(error) : Promise.resolve();
	};
};
